import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useVideo from 'flow/hooks/useVideo';
import { setConferenceMeetingCallMembers } from 'flow/actions/meeting-actions';
import {
    selectActiveMeetingChatSessionId,
    selectActiveMeetingChatSessionIsDirectMessage,
    selectActiveMeetingHasOtherParticipants,
    selectActiveMeetingHasPersistentChatHistory,
    selectActiveMeetingIsInstantMeeting,
    selectIsUserLoggedIn,
} from 'flow/selectors/meeting-selectors';
import { ChatSessionKey, ExtensionKey } from 'flow/types/EntityKeys';
import { CALLING_DURATION } from 'flow/utils/meeting-constants';

interface CallingExtension {
    extensionKey: ExtensionKey;
    callingEndTime: number;
}

interface AddParticipant {
    canInviteParticipant: boolean;
    onAddParticipant: () => void;
}

/**
 * Owns the participants panel's "Add participant" flow: opens the invite modal,
 * rings the chosen colleague (notifyCurrentVideoCall), and tracks who is
 * currently being rung. Each invite auto-expires after CALLING_DURATION so a
 * stale "calling…" row doesn't linger; the list is pushed to Redux
 * (setConferenceMeetingCallMembers), where useMeetingMembers reads it back to
 * render the rows.
 *
 * The 20s timeout and the calling list must outlive the panel being collapsed
 * and reopened mid-invite — which holds because MeetingParticipantsList (and so
 * this hook) stays mounted for the whole meeting via MeetingSidePanel, exactly
 * as it did when this lived in MeetingVideoCanvas.
 */
const useAddParticipant = (): AddParticipant => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const { openModal } = useModal();
    const { notifyCurrentVideoCall } = useVideo();

    const chatSessionIdRaw = useAppSelector(selectActiveMeetingChatSessionId);
    const isUserLoggedIn = useAppSelector(selectIsUserLoggedIn);
    // Kept fresh by fetchMeetingData's own poll (meeting-actions.js) — no need to re-fetch here too.
    const chatHistoryActive = useAppSelector(
        selectActiveMeetingHasPersistentChatHistory,
    );
    const isInstantMeeting = useAppSelector(
        selectActiveMeetingIsInstantMeeting,
    );
    const isExistingDirectMessage = useAppSelector(
        selectActiveMeetingChatSessionIsDirectMessage,
    );
    const hasOtherParticipants = useAppSelector(
        selectActiveMeetingHasOtherParticipants,
    );

    const chatSessionId = 'chatsession-'.concat(
        chatSessionIdRaw,
    ) as ChatSessionKey;

    const [callingExtensionKeys, setCallingExtensionKeys] = useState<
        CallingExtension[]
    >([]);

    useEffect(() => {
        dispatch(setConferenceMeetingCallMembers(callingExtensionKeys));
    }, [callingExtensionKeys, dispatch]);

    const removeCallingExtension = (extensionKey: ExtensionKey) => {
        setCallingExtensionKeys((prevExtensions) =>
            prevExtensions.filter(
                (prevExt) => prevExt.extensionKey !== extensionKey,
            ),
        );
    };

    const addCallingExtension = (extensionKey: ExtensionKey) => {
        const newExtension = {
            extensionKey,
            callingEndTime: Date.now() + CALLING_DURATION,
        };

        setCallingExtensionKeys((prevExtensions) => [
            ...prevExtensions,
            newExtension,
        ]);

        setTimeout(
            () => removeCallingExtension(extensionKey),
            CALLING_DURATION,
        );
    };

    // Notify needs a chat session to resolve a meeting code, so hide the button for PBX conferences and guests.
    // Hidden for an instant meeting until chat history is active (AC-1637), except while the host is alone — otherwise neither Add nor Activate would ever be reachable.
    const canInviteParticipant: boolean =
        Boolean(chatSessionIdRaw) &&
        isUserLoggedIn &&
        (!isInstantMeeting ||
            isExistingDirectMessage ||
            chatHistoryActive ||
            !hasOtherParticipants);

    const onAddParticipant = () => {
        openModal('addVideoParticipant', {
            title: i18next.t(
                'call.canvas.videoparticipants.addselectedparticipant.title',
            ),
            selectedExtensionAction: (extensionKey: ExtensionKey) => {
                notifyCurrentVideoCall(extensionKey, chatSessionId);
                addCallingExtension(extensionKey);
            },
        });
    };

    return { canInviteParticipant, onAddParticipant };
};

export default useAddParticipant;
